import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { MapPin, FileText, Phone, Mail, ShieldCheck } from "lucide-react";
import { CATEGORIES, PRICE_UNITS } from "../lib/constants";
import { StatusBadge } from "../components/StatusBadge";

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/listings/${id}`);
        setListing(data);
      } catch (e) {
        toast.error("Listing not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const sendInquiry = async () => {
    if (!user || !user.email) {
      toast.message("Please sign in to send an inquiry");
      navigate("/login");
      return;
    }
    if (!msg.trim()) {
      toast.error("Please write a short message");
      return;
    }
    setSending(true);
    try {
      await api.post("/inquiries", { listing_id: id, message: msg });
      toast.success("Inquiry sent. Our admin will facilitate the intro.");
      setMsg("");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="py-24 text-center text-muted-foreground">Loading…</div>;
  if (!listing) return <div className="py-24 text-center">Listing not found.</div>;

  const catLabel = CATEGORIES.find((c) => c.value === listing.category)?.label;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10" data-testid="listing-detail">
      <Link to="/browse" className="text-sm text-muted-foreground hover:text-primary">← Back to marketplace</Link>

      <div className="mt-4 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="flex gap-2 flex-wrap">
              <Badge className="bg-primary text-primary-foreground">{catLabel}</Badge>
              {listing.technology && <Badge variant="outline" className="capitalize">{listing.technology}</Badge>}
              {listing.listing_type === "buy" && <Badge className="bg-accent text-accent-foreground">Requirement</Badge>}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-primary tracking-tight mt-3 break-words">
              {listing.title}
            </h1>
            <div className="mt-3 flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4" /> {listing.state}
            </div>
          </div>

          {listing.image_paths?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {listing.image_paths.map((p) => (
                <div key={p} className="aspect-video rounded-xl overflow-hidden bg-secondary">
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          <Card>
            <CardContent className="p-4 sm:p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              {listing.capacity_mw ? (
                <Stat label="Capacity" value={`${listing.capacity_mw} MW`} />
              ) : null}
              {listing.land_area_acres ? (
                <Stat label="Area" value={`${listing.land_area_acres} acres`} />
              ) : null}
              {listing.connectivity_type ? (
                <Stat label="Connectivity" value={listing.connectivity_type} />
              ) : null}
              {listing.voltage_kv ? (
                <Stat label="Voltage" value={`${listing.voltage_kv} kV`} />
              ) : null}
              {listing.duration_months ? (
                <Stat label="Duration" value={`${listing.duration_months} months`} />
              ) : null}
              {listing.start_date ? <Stat label="Start" value={listing.start_date} /> : null}
              {listing.end_date ? <Stat label="End" value={listing.end_date} /> : null}
              <Stat
                label="Price"
                value={listing.price_inr ? `₹ ${Number(listing.price_inr).toLocaleString("en-IN")} (${PRICE_UNITS.find((u) => u.value === listing.price_unit)?.label || listing.price_unit})` : "On request"}
              />
            </CardContent>
          </Card>

          {listing.description && (
            <Card>
              <CardContent className="p-6">
                <div className="label-caps mb-2">Description</div>
                <p className="whitespace-pre-wrap text-foreground/90 leading-relaxed">{listing.description}</p>
              </CardContent>
            </Card>
          )}

          {listing.document_paths?.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <div className="label-caps mb-3">Documents</div>
                <ul className="space-y-2">
                  {listing.document_paths.map((p) => (
                    <li key={p}>
                      <a
                        href={fileUrl(p)}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                      >
                        <FileText className="h-4 w-4" /> {p.split("/").pop()}
                      </a>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>

        <aside className="space-y-4">
          <Card className="border-primary/20">
            <CardContent className="p-6">
              <div className="label-caps">Posted by</div>
              <div className="font-display text-xl font-semibold mt-1">{listing.owner_name || "Verified seller"}</div>
              {listing.owner_company && <div className="text-sm text-muted-foreground">{listing.owner_company}</div>}

              <div className="mt-5 border-t pt-4 space-y-2">
                {listing.owner_email ? (
                  <>
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-primary" />
                      <span data-testid="owner-email">{listing.owner_email}</span>
                    </div>
                    {listing.owner_phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-primary" />
                        <span data-testid="owner-phone">{listing.owner_phone}</span>
                      </div>
                    )}
                  </>
                ) : null}
              </div>

              {listing.status === "active" ? (
              <>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full mt-5 bg-primary hover:bg-primary/90" data-testid="inquire-btn">
                    Send inquiry
                  </Button>
                </DialogTrigger>
                <DialogContent className="w-[calc(100%-2rem)] rounded-lg">
                  <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-primary">Send inquiry</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Your inquiry is also shared with our admin team, who can help with introductions.
                    </p>
                    <Textarea
                      value={msg}
                      onChange={(e) => setMsg(e.target.value)}
                      rows={5}
                      placeholder="Share your requirement, timeline, offtake / tariff expectations…"
                      data-testid="inquiry-message"
                    />
                    <Button
                      onClick={sendInquiry}
                      disabled={sending}
                      className="w-full bg-primary hover:bg-primary/90"
                      data-testid="inquiry-submit"
                    >
                      {sending ? "Sending…" : "Submit inquiry"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 text-accent" />
                Admin-approved listing
              </div>
              </>
              ) : (
                <div className="mt-5 rounded-lg bg-secondary p-3 text-sm" data-testid="listing-not-live">
                  <StatusBadge status={listing.status} />
                  <p className="text-muted-foreground mt-2">
                    {listing.status === "pending" ? "This listing is waiting for admin approval and is not visible to other members yet." : "This listing was rejected by the admin and is not visible to other members."}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="label-caps">{label}</div>
      <div className="font-display text-lg font-semibold text-primary mt-0.5">{value}</div>
    </div>
  );
}
